/* ═══════════════════════════════════════════════════════════════
   TasksKit — the one Tasks surface entry point: Board/List view,
   search + filter state, which task is open, create-open. Replaces
   the old the former Tasks page's `projectId`/`embedded` props — embedded
   behavior (Project Canvas host) is now simply `scope.projectId`
   being set.
   ═══════════════════════════════════════════════════════════════ */
import { useEffect, useMemo, useState } from 'react';
import type { TaskStatus } from '../../../shared/types';
import type { DeepLink } from '../../types/navigation';
import { hasPermission } from '../../../shared/permissions';
import { isArchived, activeOnly } from '../../lib/archive';
import { EMPTY_FILTER_STATE, type TableFilterState } from '../../components/filterModel';
import { useTasksData } from './useTasksData';
import { canSeeTask, taskBelongsToProject } from './taskPermissions';
import { matchesTaskFilters, type TaskFilterCtx } from './taskFilterFields';
import { TasksToolbar } from './TasksToolbar';
import { TasksTable } from './TasksTable';
import { TasksBoard } from './TasksBoard';
import { TaskSheet } from './TaskSheet';
import type { ViewMode, TasksScope } from './types';
import { toAddress } from '../../routing/address';
import { useAddressSync } from '../../routing/useAddressSync';

export interface TasksKitProps {
  toast: (msg: string, isError?: boolean) => void;
  authUser: any;
  /** Project Canvas: presets the project filter on mount and whenever it
   *  changes — the same SearchableSelect the sidebar page uses still lets
   *  the user change or clear it; nothing is locked. Its presence is what
   *  used to be the `embedded` prop. */
  scope?: TasksScope;
  deepLink?: Extract<DeepLink, { view: 'tasks' }> | null;
  onDeepLinkConsumed?: () => void;
  /** Reserved for a future nested/side-panel host; unused today (gates
   *  and chrome are unchanged in this pass). */
  onBack?: () => void;
}

export function TasksKit({ toast, authUser, scope, deepLink, onDeepLinkConsumed }: TasksKitProps) {
  const embedded = Boolean(scope?.projectId);
  const data = useTasksData(authUser, toast);

  const [viewMode, setViewMode] = useState<ViewMode>('board');

  // Filters — ONE search + ONE filter state drives BOTH List and Board
  // (Decision 2). `scopeProjectId` is the "two doors" project preset
  // (Decision 1) — kept SEPARATE from `filterState` on purpose so it is
  // never written to localStorage/Prefs/FirmPrefs, unlike the user's own
  // ad-hoc filters, which persist exactly like every other table's Filter
  // menu (DataTable owns that via the controlled `filterState` below).
  const [scopeProjectId, setScopeProjectId] = useState('');
  const [filterState, setFilterState] = useState<TableFilterState>(EMPTY_FILTER_STATE);
  const [filterSearch, setFilterSearch] = useState('');

  // Detail modal
  const [detailTaskId, setDetailTaskId] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createInStatus, setCreateInStatus] = useState<TaskStatus>('todo');
  const [showArchived, setShowArchived] = useState(false);
  // Project Canvas: deep-link "open task X" once tasks have loaded
  // (race-safe — mirrors InvoicesPageNew's pendingOpenInvoiceId).
  const [pendingOpenTaskId, setPendingOpenTaskId] = useState<string | null>(null);
  // Project Canvas: deep-link "create with this project preselected" —
  // only set via the deep-link flow, never by the page's own + New buttons,
  // so manual creation keeps today's behavior exactly.
  const [createPresetProjectId, setCreatePresetProjectId] = useState<string | null>(null);

  // Permission-first. No role fallback — every gate maps to a permission
  // key so custom firm roles can grant/deny each action independently.
  const perms: string[] | undefined = authUser?.permissions;
  const can = (key: string) => hasPermission(perms, key);
  const canCreateTask = can('tasks.create');
  const canArchiveTask = can('tasks.archive');

  // Address: the open task's address; Back from it closes the panel. Off when
  // embedded in a project (the host owns the address there).
  const openTask = detailTaskId ? data.tasks.find((t) => t.id === detailTaskId) : undefined;
  useAddressSync({
    address: openTask ? toAddress({ view: 'tasks', taskId: openTask.id }) : null,
    listPath: '/tasks',
    onLeave: () => setDetailTaskId(null),
    title: openTask?.title ?? null,
    disabled: embedded,
  });

  // Deep link: a project-scoped arrival pre-selects the project filter.
  // One-shot — waits for projects to load so the filter's SearchableSelect
  // has the option to show, then consumes the link so it never re-fires.
  useEffect(() => {
    if (!deepLink) return;
    if (deepLink.projectId) {
      if (!data.projectsLoaded) return; // wait for the load attempt to settle
      setScopeProjectId(deepLink.projectId);
    }
    if (deepLink.create) {
      setCreatePresetProjectId(deepLink.projectId || null);
      setCreateInStatus('todo');
      setShowCreateModal(true);
    }
    if (deepLink.taskId) setPendingOpenTaskId(deepLink.taskId);
    // Embedded hosts (Project Canvas) own their own deep-link slot — the
    // onDeepLinkConsumed callback assumes the app shell's one-shot pattern.
    if (!embedded) onDeepLinkConsumed?.();
  }, [deepLink, data.projectsLoaded, embedded, onDeepLinkConsumed]);

  // Project Canvas: host-provided project scope presets the filter — same
  // control as the sidebar page, so the user can still change/clear it
  // (Decision 1: visible as an active filter, clearable, never persisted).
  useEffect(() => {
    if (scope?.projectId) setScopeProjectId(scope.projectId);
  }, [scope?.projectId]);

  // Once tasks have loaded, satisfy a pending deep-link "open task X"
  // (a notification bell click or the assignment-email link). A task the
  // server no longer returns — deleted, or no longer visible to this
  // viewer — is a normal toast, never a crash.
  useEffect(() => {
    if (!pendingOpenTaskId || data.tasks.length === 0) return;
    const target = data.tasks.find((t) => t.id === pendingOpenTaskId);
    if (target) setDetailTaskId(target.id);
    else toast('That task is no longer available', true);
    setPendingOpenTaskId(null);
  }, [pendingOpenTaskId, data.tasks, toast]);

  // ─── Visibility filtering ───
  // Per-task visibility now runs through taskPermissions' canSeeTask
  // (org-chart-based for view_team) instead of the old
  // visibleMemberIds/isTaskVisible team-leader approximation — the
  // controls that GATE actions on screen are unchanged this pass.
  const visibleTasks = useMemo(() => {
    let filtered = data.tasks.filter((t) =>
      canSeeTask(perms, data.userId, data.members, t, data.assignments, data.tasks)
    );

    // "Two doors" project scope (Decision 1) — never part of the persisted
    // `filterState` below.
    if (scopeProjectId) {
      filtered = filtered.filter((t) =>
        taskBelongsToProject(t, data.taskProjectLinks, scopeProjectId)
      );
    }
    if (filterSearch) {
      const q = filterSearch.toLowerCase();
      filtered = filtered.filter(
        (t) => t.title.toLowerCase().includes(q) || (t.description || '').toLowerCase().includes(q)
      );
    }

    return filtered;
  }, [
    data.tasks,
    data.assignments,
    data.members,
    data.taskProjectLinks,
    data.userId,
    perms,
    scopeProjectId,
    filterSearch,
  ]);

  // Standard Filter menu (Decision 3: Status/Priority/Project/Assignee/Due,
  // available to everyone who can see tasks). List gets this automatically
  // — `filterState` is passed to TasksTable's DataTable as a CONTROLLED
  // value, which applies it internally exactly like every other table's
  // Filter button. Board has no DataTable of its own, so it is applied here
  // with the SAME predicate (`matchesTaskFilters`) — one state, one rule
  // set, both views.
  const filterCtx: TaskFilterCtx = useMemo(
    () => ({
      getProjectIdsForTask: data.getProjectIdsForTask,
      getAssigneeIdsForTask: (taskId: string) => data.getAssigneesForTask(taskId).map((m) => m.id),
    }),
    [data.getProjectIdsForTask, data.getAssigneesForTask]
  );

  // Only show top-level tasks (not subtasks) in the main view.
  // Archived tasks never clutter the Board (it has no archive toggle of its
  // own) — they only resurface in the List view, and only with "Show
  // archived" on.
  const topLevelTasksAll = visibleTasks.filter((t) => !t.parentTaskId);
  const topLevelTasksUnfiltered = topLevelTasksAll.filter((t) => !isArchived(t));
  const topLevelTasks = useMemo(
    () => topLevelTasksUnfiltered.filter((t) => matchesTaskFilters(t, filterState, filterCtx)),
    [topLevelTasksUnfiltered, filterState, filterCtx]
  );
  const listTasks = activeOnly(topLevelTasksAll, showArchived);
  const countFilterMatches = (state: TableFilterState) =>
    topLevelTasksUnfiltered.reduce(
      (n, t) => (matchesTaskFilters(t, state, filterCtx) ? n + 1 : n),
      0
    );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        padding: 'var(--space-5)',
        gap: 'var(--space-4)',
      }}
    >
      <TasksToolbar
        taskCount={topLevelTasks.length}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        canCreateTask={canCreateTask}
        onNewTask={() => {
          setCreateInStatus('todo');
          setShowCreateModal(true);
        }}
        projects={data.projects}
        members={data.members}
        filterSearch={filterSearch}
        onFilterSearchChange={setFilterSearch}
        scopeProjectId={scopeProjectId}
        scopeProjectName={scopeProjectId ? data.getProjectName(scopeProjectId) : ''}
        onClearScopeProject={() => setScopeProjectId('')}
        filterState={filterState}
        onFilterStateChange={setFilterState}
        totalRows={topLevelTasksUnfiltered.length}
        countFilterMatches={countFilterMatches}
        showArchived={showArchived}
        onToggleShowArchived={() => setShowArchived((v) => !v)}
      />

      {/* Content */}
      {viewMode === 'board' ? (
        <TasksBoard
          data={data}
          tasks={topLevelTasks}
          perms={perms}
          canCreateTask={canCreateTask}
          canArchiveTask={canArchiveTask}
          onCardClick={setDetailTaskId}
          onAddClick={(status) => {
            setCreateInStatus(status);
            setShowCreateModal(true);
          }}
        />
      ) : (
        <TasksTable
          data={data}
          tasks={listTasks}
          perms={perms}
          canArchiveTask={canArchiveTask}
          filterState={filterState}
          onFilterStateChange={setFilterState}
          onRowClick={setDetailTaskId}
        />
      )}

      {/* Task Sheet — ONE panel for create AND view/edit (Decision 4).
          Stays mounted always (never `{open && <TaskSheet/>}`) so Radix's
          own close transition and focus-return-to-trigger actually run
          (DESIGN.md §7) — same pattern as ProjectFormPanel. */}
      <TaskSheet
        data={data}
        open={showCreateModal || Boolean(detailTaskId)}
        mode={showCreateModal ? 'create' : 'view'}
        task={detailTaskId ? (data.tasks.find((t) => t.id === detailTaskId) ?? null) : null}
        createInStatus={createInStatus}
        createPresetProjectId={createPresetProjectId}
        perms={perms}
        onClose={() => {
          setShowCreateModal(false);
          setDetailTaskId(null);
        }}
        clearPreset={() => setCreatePresetProjectId(null)}
      />
    </div>
  );
}

(window as any).TasksKit = TasksKit;
